import { ReservationForm } from '../validators';
import { Reservation } from '../../../types/reservation';
import { ReservationType } from '../../../types/reservationType';
import { capitalize } from '../../../utils/textUtils';
import { ReservationEvent } from '../types';

export const isSlotAvailable = (start: Date, end: Date, existingEvents: ReservationEvent[]): boolean => {
  // No permitir reservas de duración 0
  if (start.getTime() === end.getTime()) return false;

  // Ordenar eventos por fecha de inicio
  const sortedEvents = [...existingEvents].sort((a, b) => a.start.getTime() - b.start.getTime());

  // Comprobar si hay algún evento que se solape
  return !sortedEvents.some((event) => {
    // Redondear todos los timestamps al segundo más cercano
    const eventStart = Math.floor(event.start.getTime() / 1000) * 1000;
    const eventEnd = Math.floor(event.end.getTime() / 1000) * 1000;
    const newStart = Math.floor(start.getTime() / 1000) * 1000;
    const newEnd = Math.floor(end.getTime() / 1000) * 1000;

    // Permitir eventos que empiecen exactamente cuando termina otro
    // o que terminen exactamente cuando empieza otro
    if (newStart === eventEnd || newEnd === eventStart) {
      return false;
    }

    // Hay solapamiento si el nuevo evento empieza antes de que termine el existente
    // Y termina después de que empiece el existente
    return newStart < eventEnd && newEnd > eventStart;
  });
};

// Redondea una fecha al bloque de media hora más cercano o al inicio de hora si se requiere
const roundToNearestBlock = (date: Date, startsAtBegginingOfHour: boolean = false): Date => {
  if (startsAtBegginingOfHour) {
    // Si debe comenzar al inicio de la hora, redondear a la hora completa
    return new Date(date.setMinutes(0, 0, 0));
  }
  // De lo contrario, redondear a bloques de media hora
  const minutes = date.getMinutes();
  const roundedMinutes = minutes >= 30 ? 30 : 0;
  return new Date(date.setMinutes(roundedMinutes, 0, 0));
};

// Definir una constante para el tiempo máximo del calendario
const CLICK_EVENT_PREFERRED_DURATION = 60;

// Encuentra el espacio disponible más cercano para una reserva
export const findNearestAvailableSlot = (
  clickedTime: Date,
  existingEvents: ReservationEvent[],
  maxTime?: Date,
  startsAtBegginingOfHour: boolean = false,
): { start: Date; end: Date } => {
  const sortedEvents = [...existingEvents].sort((a, b) => a.start.getTime() - b.start.getTime());
  const clickedTimeMs = clickedTime.getTime();

  // Primero intentamos crear la reserva exactamente donde el usuario ha clickado
  const roundedStart = roundToNearestBlock(clickedTime, startsAtBegginingOfHour);

  // Calcular el tiempo final teniendo en cuenta el límite máximo si existe
  let endTime = new Date(roundedStart.getTime() + CLICK_EVENT_PREFERRED_DURATION * 60 * 1000);

  // Si hay un tiempo máximo definido, verificar que no lo exceda
  if (maxTime) {
    const maxTimeForDay = new Date(roundedStart);
    maxTimeForDay.setHours(maxTime.getHours(), maxTime.getMinutes(), 0, 0);

    if (endTime > maxTimeForDay) {
      endTime = maxTimeForDay;
    }
  }

  const defaultSlot = {
    start: roundedStart,
    end: endTime,
  };

  // Si este slot está disponible, lo usamos
  if (isSlotAvailable(defaultSlot.start, defaultSlot.end, existingEvents)) {
    return defaultSlot;
  }

  // Si no está disponible, buscamos el hueco más cercano
  let prevEvent: ReservationEvent | null = null;
  let nextEvent: ReservationEvent | null = null;

  for (let i = 0; i < sortedEvents.length; i++) {
    if (sortedEvents[i].start.getTime() > clickedTimeMs) {
      nextEvent = sortedEvents[i];
      prevEvent = i > 0 ? sortedEvents[i - 1] : null;
      break;
    }
    if (i === sortedEvents.length - 1) {
      prevEvent = sortedEvents[i];
    }
  }

  // Si hay eventos antes y después, encontrar el espacio máximo disponible
  if (prevEvent && nextEvent) {
    const availableSpace = nextEvent.start.getTime() - prevEvent.end.getTime();
    const duration = Math.min(availableSpace, CLICK_EVENT_PREFERRED_DURATION * 60 * 1000);

    // Si el click está más cerca del final del evento previo, crear después de él
    const distanceToPrev = clickedTimeMs - prevEvent.end.getTime();
    const distanceToNext = nextEvent.start.getTime() - clickedTimeMs;

    if (distanceToPrev < distanceToNext) {
      const start = startsAtBegginingOfHour ? roundToNearestBlock(prevEvent.end, true) : prevEvent.end;

      return {
        start,
        end: new Date(start.getTime() + duration),
      };
    }

    const start = startsAtBegginingOfHour
      ? roundToNearestBlock(new Date(nextEvent.start.getTime() - duration), true)
      : new Date(nextEvent.start.getTime() - duration);

    return {
      start,
      end: nextEvent.start,
    };
  }

  // Si hay un evento previo pero no siguiente, intentar crear después del previo
  if (prevEvent) {
    const start = startsAtBegginingOfHour ? roundToNearestBlock(prevEvent.end, true) : prevEvent.end;

    let end = new Date(start.getTime() + CLICK_EVENT_PREFERRED_DURATION * 60 * 1000);

    // Verificar límite máximo
    if (maxTime) {
      const maxTimeForDay = new Date(start);
      maxTimeForDay.setHours(maxTime.getHours(), maxTime.getMinutes(), 0, 0);

      if (end > maxTimeForDay) {
        end = maxTimeForDay;
      }
    }

    return {
      start,
      end,
    };
  }

  // Si hay un evento siguiente pero no previo, intentar crear antes del siguiente
  if (nextEvent) {
    const start = startsAtBegginingOfHour
      ? roundToNearestBlock(new Date(nextEvent.start.getTime() - CLICK_EVENT_PREFERRED_DURATION * 60 * 1000), true)
      : new Date(nextEvent.start.getTime() - CLICK_EVENT_PREFERRED_DURATION * 60 * 1000);

    return {
      start,
      end: nextEvent.start,
    };
  }

  // Si no hay eventos cercanos, usar el slot por defecto
  return defaultSlot;
};

export const getHourPickerEventTitle = (
  reservationForm: ReservationForm | undefined,
  reservationType: ReservationType | undefined,
  newEvent: boolean,
) => {
  if (newEvent) return 'Nueva reserva';
  if (!reservationForm) return 'Reserva existente'; // Fallback name
  return reservationType?.groupSize === 1
    ? `${capitalize(reservationType?.name)} - ${capitalize(reservationForm.externalUsers?.[0]?.name)} ${capitalize(reservationForm.externalUsers?.[0]?.surnames)}`
    : `${capitalize(reservationType?.name)}`;
};

export const getBlockedEvents = (
  reservations: Reservation[] | undefined,
  reservationTypeFromId: (reservationTypeId: string) => ReservationType | undefined,
  currentReservationId?: string,
): ReservationEvent[] => {
  if (!reservations?.length) return [];

  return reservations
    .filter((reservation) => reservation.id !== currentReservationId)
    .map((reservation) => {
      const reservationType = reservationTypeFromId(reservation.reservationTypeId);
      const title = getHourPickerEventTitle(reservation, reservationType, false);

      // Crear nuevas fechas y asegurarnos de que los milisegundos están a 0
      const start = new Date(reservation.localStartDate);
      start.setMilliseconds(0);
      const end = new Date(reservation.localEndDate);
      end.setMilliseconds(0);

      return {
        id: reservation.id,
        title,
        start,
        end,
        isBlocked: true,
      };
    });
};
